import { useState } from 'react'
import dot from 'dot-object'
import isEqual from 'lodash.isequal'
import Formsy from 'formsy-react'
import { userSelector } from 'auth/selectors'
import { updateAccountPendingSelector } from 'dashboard-account/selectors'
import { updateAccount } from 'dashboard-account/actions'
import UserDetails from 'dashboard-account/components/UserDetails'
import Button from '@components/Button'
import CheckBox from '@components/Checkbox'
import ErrorMessage from '@components/ErrorMessage'
import useRequestPending from 'shared/hooks/useRequestPending'
import featureToggles from 'shared/featureToggles'

import { AddressFormBr } from 'shared/components/AddressForm/AddressFormBr'
import { AddressFormCa } from 'shared/components/AddressForm/AddressFormCa'

const AccountForm = ({
    user: { details },
    updateAccountPending,
    translate: t,
    updateAccount,
    hiddenUserDetails,
    hiddenDeliveryAddress,
    updatedAccount,
    setUpdatedAccount,
}) => {
    const [useBillingForDelivery, setUseBillingForDelivery] = useState(
        isEqual(details.billingAddress, details.deliveryAddress)
    )
    const [userDetails, setUserDetails] = React.useState(details)
    const isStateFieldExcluded = details.excludeFields.includes('ACCOUNTSTATE')
    const { error, isLoading } = useRequestPending(updateAccountPending, () => setUpdatedAccount(true))
    const submit = data => {
        setUpdatedAccount(false)
        const formDetails = dot.object(data)

        const { billingAddress, deliveryAddress, firstName, lastName, phone, emailAddress, document } = {
            ...details,
            ...formDetails,
        }

        const updatedData = {
            billingAddress,
            deliveryAddress: useBillingForDelivery ? billingAddress : deliveryAddress,
            firstName,
            lastName,
            phone,
            emailAddress,
            document,
        }

        updateAccount(updatedData)
    }

    const onCheckboxChange = () => {
        setUseBillingForDelivery(!useBillingForDelivery)
    }

    return (
        <Formsy onSubmit={submit}>
            {!hiddenUserDetails && (
                <>
                    <h3 className="font-weight-bold">
                        <T>dashboard.account.label</T>
                    </h3>
                    <UserDetails
                        firstName={details.firstName}
                        lastName={details.lastName}
                        phone={details.phone}
                        email={details.emailAddress}
                        translate={t}
                    />
                    <hr />
                </>
            )}
            {!hiddenDeliveryAddress && (
                <h3 className="mb-4 font-weight-bold">
                    <T>dashboard.account.billing.address</T>
                </h3>
            )}
            {featureToggles.caPurchaseFlow ? (
                <AddressFormCa
                    {...details.billingAddress}
                    country={details.billingAddress.country || undefined}
                    prefix="billingAddress"
                    translate={t}
                />
            ) : (
                <AddressFormBr
                    {...details.billingAddress}
                    country={details.billingAddress.country || undefined}
                    prefix="billingAddress"
                    isStateFieldExcluded={isStateFieldExcluded}
                    translate={t}
                />
            )}
            <div className="form-check mt-3">
                <CheckBox
                    checked={useBillingForDelivery}
                    onChange={onCheckboxChange}
                    label={t('dashboard.account.same.address')}
                />
            </div>
            {!useBillingForDelivery && !hiddenDeliveryAddress && (
                <div>
                    <hr />
                    <h3 className="mb-4 mt-4">
                        <T>dashboard.account.delivery.address</T>
                    </h3>
                    {featureToggles.caPurchaseFlow ? (
                        <AddressFormCa
                            {...details.deliveryAddress}
                            country={details.deliveryAddress.country || undefined}
                            prefix="deliveryAddress"
                            translate={t}
                        />
                    ) : (
                        <AddressFormBr
                            {...details.deliveryAddress}
                            country={details.deliveryAddress.country || undefined}
                            prefix="deliveryAddress"
                            isStateFieldExcluded={isStateFieldExcluded}
                            translate={t}
                        />
                    )}
                </div>
            )}
            <div className="mt-5">
                <Button type="submit" isLoading={isLoading} disabled={isLoading}>
                    <T>dashboard.account.save</T>
                </Button>
                {updatedAccount && !error && (
                    <div className="text-green text-center mt-3">
                        <T>dashboard.account.updated-successfully</T>
                    </div>
                )}
                {error && <ErrorMessage className="mt-2" messages={[error]} />}
            </div>
        </Formsy>
    )
}

export default compose(
    withTranslate,
    connect(
        createStructuredSelector({
            user: userSelector,
            updateAccountPending: updateAccountPendingSelector,
        }),
        {
            updateAccount,
        }
    )
)(AccountForm)
